import React, { useCallback, useEffect, useRef } from "react";

import styles from "./index.module.scss";

/**
 * How many pixels of the slide the following block takes to ease up from
 * off-screen into its locked position beneath the cards. Larger = gentler.
 */
const REVEAL_ENTRY_DISTANCE = 320;
interface Props {
    /**
     * Content rendered inside the pinned container but above the
     * horizontally-scrolling area. Pins with the cards but doesn't translate.
     */
    header?: React.ReactNode;
    /**
     * Render prop was required to make the active index thing work properly.
     * I don't love that it's here; if future work would benefit from this being
     * a union type with a normal children prop then we should do that.
     *
     * Attach this ref to the element whose direct children are the
     * horizontally-scrolling items so onActiveIndexChange can iterate
     * inner.children to find the child that is centered in the viewport
     */
    children: (innerRef: React.RefCallback<HTMLElement>) => React.ReactNode;
    /**
     * Called during the scroll animation with the index of the child whose
     * center is closest to the viewport center.
     */
    onActiveIndexChange?: (index: number) => void;
    /**
     * The element revealed into the excess space below the pinned content,
     * translated up as the scroll progresses (only while capped — see
     * maxTopGap), then handed back to normal flow. Provide to enable the
     * reveal; omit to disable. Captured on mount, so it must point to a stable
     * element — a `.current` that changes afterward is not tracked.
     */
    revealTarget?: React.RefObject<HTMLElement | null>;
    /**
     * Element whose margin-bottom reserves the reveal target's vacated flow
     * space while it is fixed, keeping page height constant. Usually the block's
     * section wrapper. Only used with revealTarget. Captured on mount, so it
     * must point to a stable element.
     */
    spaceReservationTarget?: React.RefObject<HTMLElement | null>;
    /**
     * Caps the space above the vertically-centered content. Beyond this gap the
     * content pins here instead of floating further down, and the excess falls
     * below for revealTarget to fill. Omit for uncapped centering.
     */
    maxTopGap?: number;
}

export const HorizontalScrollWrapper = (props: Props) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const inner = useRef<HTMLElement | null>(null);
    const maxHorizontalOffset = useRef(0);
    const isScrolling = useRef(false);
    // Mutating a DOM node read directly off props trips react-hooks/immutability,
    // so mirror the consumer's target refs into ones we own (captured on mount).
    const revealEl = useRef<HTMLElement | null>(null);
    const reservationEl = useRef<HTMLElement | null>(null);
    const revealTargetHeight = useRef(0);

    /**
     * The reveal target is fixed while revealed, so it leaves normal flow.
     * Reserve its height here to keep total page height constant — otherwise
     * scrollbar dragging jumps at the reveal threshold.
     */
    const reserveRevealSpace = (reserve: boolean) => {
        if (!reservationEl.current) return;
        reservationEl.current.style.marginBottom = reserve
            ? `${revealTargetHeight.current}px`
            : "";
    };

    // Return the reveal target to normal flow and release its reserved space.
    const clearReveal = () => {
        const next = revealEl.current;
        if (next) {
            next.style.position = "";
            next.style.top = "";
            next.style.left = "";
            next.style.width = "";
        }
        reserveRevealSpace(false);
    };

    /**
     * The amount that we want to scroll horizontally is mostly dependent on the width of the
     * horizontally scrolling content, but we don't want it to scroll completely out of view.
     * That's why we subtract the window width. Margins aren't part of width calculations
     * which is why it's also a part of this.
     */
    const setMaxHorizontalOffset = (element: HTMLElement | null) => {
        if (element && element.firstElementChild) {
            maxHorizontalOffset.current =
                element.scrollWidth -
                window.innerWidth +
                parseInt(
                    window.getComputedStyle(element.firstElementChild)
                        .marginLeft,
                );
        }
    };

    /**
     * The useCallback approach avoids the problem where you can't do something
     * once the ref changes from null because refs don't trigger a render
     */
    const innerRef = useCallback((element: HTMLElement) => {
        if (element !== null) {
            setMaxHorizontalOffset(element);
            inner.current = element;
        }
    }, []);

    /**
     * The header and cards center as one group. innerOffsetFromRoot is the
     * header height (the cards' offset within the pinned content); pinTop is the
     * resulting gap above the group. maxTopGap caps that gap so on tall
     * viewports the content stops floating down and the excess falls below the
     * cards, where revealTarget can fill it.
     */
    const getPinGeometry = (
        innerOffsetFromRoot: number,
        innerHeight: number,
    ) => {
        const groupHeight = innerOffsetFromRoot + innerHeight;
        const centeredGap = Math.max(0, (window.innerHeight - groupHeight) / 2);
        const cap = props.maxTopGap;
        const pinTop = cap != null ? Math.min(centeredGap, cap) : centeredGap;
        const capped = pinTop < centeredGap;
        const horizontalScrollStart = window.innerHeight - innerOffsetFromRoot;

        return {
            pinTop,
            capped,
            // group + a bottom gap of pinTop, so the gaps above and below match
            // and the content centers in the dark region; reveal and unpin both
            // key off this, keeping the handoff continuous.
            footprintHeight: groupHeight + pinTop,
            prePinScrollDistance: Math.max(0, horizontalScrollStart - pinTop),
        };
    };

    const updateScrollTransforms = () => {
        if (!contentRef.current || !rootRef.current || !inner.current) {
            isScrolling.current = false;
            return;
        }

        const rootBox = rootRef.current.getBoundingClientRect();
        const innerBox = inner.current.getBoundingClientRect();
        const innerHeight = inner.current.offsetHeight;
        const innerOffsetFromRoot = inner.current.offsetTop;

        const { pinTop, capped, footprintHeight, prePinScrollDistance } =
            getPinGeometry(innerOffsetFromRoot, innerHeight);

        let horizontalOffset = 0;
        let verticalOffset = 0;
        const pinnedScrollProgress = Math.max(0, pinTop - rootBox.top);
        const prePinScrollProgress = Math.min(
            prePinScrollDistance,
            Math.max(0, window.innerHeight - innerBox.top),
        );
        const scrollProgress =
            rootBox.top < pinTop
                ? prePinScrollDistance + pinnedScrollProgress
                : prePinScrollProgress;

        if (scrollProgress < maxHorizontalOffset.current) {
            horizontalOffset = -scrollProgress;
        } else {
            horizontalOffset = -maxHorizontalOffset.current;
        }

        const positionRevealTarget = (revealScrollProgress: number) => {
            const next = revealEl.current;
            if (!next) return;

            const restingTop = pinTop + footprintHeight;
            const t = Math.min(1, revealScrollProgress / REVEAL_ENTRY_DISTANCE);
            const eased = 1 - (1 - t) * (1 - t); // easeOutQuad
            const top =
                restingTop + (1 - eased) * (window.innerHeight - restingTop);

            next.style.position = "fixed";
            next.style.top = `${top}px`;
            next.style.left = `${rootBox.left}px`;
            next.style.width = `${rootBox.width}px`;
        };

        /**
         * Pin when the cards would be vertically centered in the viewport.
         * pinTop accounts for the header height above the cards.
         */
        if (rootBox.top < pinTop) {
            /**
             * Fixed positioning was more reliable than sticky positioning, and
             * more performant than relative positioning with translateY working
             * against the scroll
             */
            contentRef.current.style.position = "fixed";
            contentRef.current.style.top = `${pinTop}px`;

            /** This controls the vertical scroll once you're past the block */
            if (rootBox.bottom < footprintHeight + pinTop) {
                verticalOffset = rootBox.bottom - footprintHeight - pinTop;
            }

            /**
             * Reveal only when capped: only then is there excess space below
             * the cards to fill. While the content is centered, revealing would
             * push it off-center. On handoff (verticalOffset < 0) the element
             * returns to normal flow.
             */
            if (revealEl.current && capped && verticalOffset >= 0) {
                positionRevealTarget(pinnedScrollProgress);
                reserveRevealSpace(true);
            } else {
                clearReveal();
            }
        } else {
            contentRef.current.style.position = "relative";
            contentRef.current.style.top = "0";
            clearReveal();
        }

        /**
         * You should never mutate the DOM like this in React. HOWEVER, this delivers
         * an immediate and thorough performance gain compared to useState.
         */
        contentRef.current.style.transform = `translate3d(0, ${verticalOffset}px, 0)`;
        inner.current.style.transform = `translate3d(${horizontalOffset}px, 0, 0)`;

        // Determine which child is closest to the viewport center
        if (props.onActiveIndexChange) {
            const viewportCenter = window.innerWidth / 2;
            const children = inner.current.children;
            let closestIndex = 0;
            let closestDistance = Infinity;
            for (let i = 0; i < children.length; i++) {
                const child = children[i] as HTMLElement;
                const childCenter =
                    child.offsetLeft + child.offsetWidth / 2 + horizontalOffset;
                const distance = Math.abs(childCenter - viewportCenter);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = i;
                }
            }
            props.onActiveIndexChange(closestIndex);
        }

        isScrolling.current = false;
    };

    const onScroll = () => {
        if (!isScrolling.current) {
            isScrolling.current = true;
            window.requestAnimationFrame(updateScrollTransforms);
        }
    };

    const onResize = () => {
        setMaxHorizontalOffset(inner.current);
        // Recapture the reserved height while the element is in normal flow; a
        // fixed element's measured height would reflect its overridden width.
        if (revealEl.current && revealEl.current.style.position !== "fixed") {
            revealTargetHeight.current = revealEl.current.offsetHeight;
        }
        if (rootRef.current && contentRef.current && inner.current) {
            const innerHeight = inner.current.offsetHeight;
            const { pinTop, footprintHeight, prePinScrollDistance } =
                getPinGeometry(inner.current.offsetTop, innerHeight);
            rootRef.current.style.height = `${
                Math.max(
                    0,
                    maxHorizontalOffset.current - prePinScrollDistance,
                ) +
                footprintHeight +
                pinTop
            }px`;
        }
    };

    const jumpToChild = (child: HTMLElement) => {
        if (!rootRef.current) return;
        const offset = child.offsetLeft;
        const marginLeft = parseInt(getComputedStyle(child).marginLeft);
        const rootTop = rootRef.current.offsetTop;
        window.scrollTo({
            top: rootTop + offset - marginLeft,
            behavior: "smooth",
        });
    };

    /**
     * When focusable content is tabbed to, we need to scroll it into view
     */
    const onFocusIn = (event: FocusEvent) => {
        if (!inner.current) return;
        const target = event.target as HTMLElement;
        // Find the direct child of inner that contains the focused element
        const child = Array.from(inner.current.children).find((c) =>
            c.contains(target),
        );
        if (child instanceof HTMLElement) jumpToChild(child);
    };

    /**
     * Tabbing wants to put an element in view by default but our custom scroll
     * doesn't work that way. You can't preventDefault a scroll but apparently
     * you can reset it.
     */
    const onContentScroll = (event: Event) => {
        if (!(event.target instanceof HTMLElement)) return;
        event.target.scrollTo({ top: 0, left: 0 });
    };

    useEffect(() => {
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onResize, { passive: true });
        if (contentRef.current) {
            contentRef.current.addEventListener("scroll", onContentScroll, {
                passive: true,
            });
        }
        if (inner.current) {
            inner.current.addEventListener("focusin", onFocusIn);
        }

        // Capture the consumer's target refs locally; record the reveal
        // target's natural height for space reservation.
        revealEl.current = props.revealTarget?.current ?? null;
        reservationEl.current = props.spaceReservationTarget?.current ?? null;
        if (revealEl.current) {
            revealTargetHeight.current = revealEl.current.offsetHeight;
        }

        setMaxHorizontalOffset(inner.current);
        onResize();
        updateScrollTransforms();

        return () => {
            clearReveal();
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onResize);
            if (contentRef.current) {
                contentRef.current.removeEventListener(
                    "scroll",
                    onContentScroll,
                );
            }
            if (inner.current) {
                inner.current.removeEventListener("focusin", onFocusIn);
            }
        };
    }, []);

    return (
        <div ref={rootRef} className={styles.root}>
            <div className={styles.content} ref={contentRef}>
                {props.header}
                {/* eslint-disable-next-line react-hooks/refs -- innerRef is a stable callback ref passed to the render-prop; it does not read a ref value during render */}
                {props.children(innerRef)}
            </div>
        </div>
    );
};
